import React, { useState } from 'react'

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  Text,
  Heading,
  Stack,
  Divider,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  Grid,
  GridItem,
  useToast,
} from '@chakra-ui/react'

const NYCHA_WORK_ORDER_URL =
  'https://my.nycha.info/PublicSite/WorkOrder/WorkOrderRetrieval'
const TOAST_DELAY_MS = 1500

const parseNYCHAResponse = html => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')

  const errorMessage = doc.querySelector('p')
  if (
    errorMessage &&
    errorMessage.textContent.includes(
      'The ticket number you entered was not found'
    )
  ) {
    return {
      notFound: true,
      message:
        'The ticket number you entered was not found. Please check your records and try again. Please note that historical information about closed tickets is limited to those closed within the past 12 months.',
    }
  }

  const fieldMap = {
    Location: 'location',
    Description: 'description',
    'Status Description': 'statusDescription',
    'Development Name': 'developmentName',
    'Creation Date': 'creationDate',
    'Status Date': 'statusDate',
    'Scheduled Date': 'scheduledDate',
    'Action Taken Message': 'actionTaken',
    'Original Ticket Number': 'originalTicketNumber',
  }

  const extractFields = rows => {
    const data = {}
    rows.forEach(row => {
      row.querySelectorAll('.col-md-4').forEach(col => {
        const heading = col.querySelector('h3')
        if (heading) {
          const headingText = heading.textContent.trim()
          const valueDiv = col.querySelector('div')
          const fieldName = fieldMap[headingText]
          if (fieldName && valueDiv) {
            data[fieldName] = valueDiv.textContent.trim()
          }
        }
      })
    })
    return data
  }

  const originalTicket = {
    ticketNumber: doc.querySelector('.ticketnumber')?.textContent.trim() || '',
    ...extractFields(doc.querySelectorAll('.row')),
  }

  const affiliatedTickets = []
  doc.querySelectorAll('.collapse').forEach(section => {
    const ticket = extractFields(section.querySelectorAll('.row'))
    const triggerRow = section.previousElementSibling
    if (triggerRow) {
      const ticketNumDiv = triggerRow.querySelector('.ticketnumber')
      if (ticketNumDiv) {
        ticket.ticketNumber = ticketNumDiv.textContent.replace(/[^0-9]/g, '')
      }
    }
    if (ticket.ticketNumber) {
      affiliatedTickets.push(ticket)
    }
  })

  return {
    originalTicket,
    affiliatedTickets,
    hasAffiliatedTickets: affiliatedTickets.length > 0,
  }
}

const NYCHAModal = ({ isOpen, onClose, workOrderNumber }) => {
  const [ticketData, setTicketData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const toast = useToast()

  const fetchNYCHAData = async () => {
    setLoading(true)
    setError(null)
    try {
      const formData = new FormData()
      formData.append('WorkOrderNum', workOrderNumber)

      const response = await fetch(NYCHA_WORK_ORDER_URL, {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        throw new Error('Failed to fetch NYCHA work order')
      }

      const html = await response.text()
      const parsedData = parseNYCHAResponse(html)
      setTicketData(parsedData)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  React.useEffect(() => {
    if (isOpen && workOrderNumber) {
      fetchNYCHAData()
    }

    if (!isOpen) {
      setTicketData(null)
      setError(null)
    }
  }, [isOpen, workOrderNumber])

  const renderTicketInfo = (label, value) => {
    if (!value) return null
    return (
      <GridItem>
        <Text fontWeight='bold' fontSize='sm' color='gray.600'>
          {label}
        </Text>
        <Text>{value}</Text>
      </GridItem>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='xl' scrollBehavior='inside'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>NYCHA Ticket: {workOrderNumber}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {loading && (
            <Box textAlign='center' py={10}>
              <Spinner size='xl' />
              <Text mt={4}>Loading ticket information...</Text>
            </Box>
          )}

          {error && (
            <Alert status='error'>
              <AlertIcon />
              {error}
            </Alert>
          )}

          {ticketData?.notFound && (
            <Alert status='warning'>
              <AlertIcon />
              <Box>
                <Text fontWeight='bold' mb={2}>
                  Ticket Not Found
                </Text>
                <Text>{ticketData.message}</Text>
              </Box>
            </Alert>
          )}

          {ticketData && !loading && !ticketData.notFound && (
            <Stack spacing={6}>
              {/* Original Ticket */}
              <Box>
                <Heading size='md' mb={3}>
                  Original Ticket
                </Heading>
                <Box p={4} borderWidth={1} borderRadius='md' bg='gray.50'>
                  <Badge
                    colorScheme='blue'
                    fontSize='lg'
                    mb={3}
                    borderRadius={5}
                  >
                    {ticketData.originalTicket.ticketNumber}
                  </Badge>
                  <Grid templateColumns='repeat(3, 1fr)' gap={4}>
                    {renderTicketInfo(
                      'Location',
                      ticketData.originalTicket.location
                    )}
                    {renderTicketInfo(
                      'Description',
                      ticketData.originalTicket.description
                    )}
                    {renderTicketInfo(
                      'Status Date',
                      ticketData.originalTicket.statusDate
                    )}
                    {renderTicketInfo(
                      'Development Name',
                      ticketData.originalTicket.developmentName
                    )}
                    {renderTicketInfo(
                      'Creation Date',
                      ticketData.originalTicket.creationDate
                    )}
                    {renderTicketInfo(
                      'Status Description',
                      ticketData.originalTicket.statusDescription
                    )}
                    {renderTicketInfo(
                      'Scheduled Date',
                      ticketData.originalTicket.scheduledDate
                    )}
                  </Grid>
                  {ticketData.originalTicket.actionTaken && (
                    <Box mt={3}>
                      <Text fontWeight='bold' fontSize='sm' color='gray.600'>
                        Action Taken Message
                      </Text>
                      <Text>{ticketData.originalTicket.actionTaken}</Text>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Affiliated Tickets */}
              {ticketData.hasAffiliatedTickets && (
                <>
                  <Divider />
                  <Box>
                    <Heading size='md' mb={3}>
                      Affiliated Ticket(s)
                    </Heading>
                    <Stack spacing={4}>
                      {ticketData.affiliatedTickets.map((ticket, index) => (
                        <Box
                          key={index}
                          p={4}
                          borderWidth={1}
                          borderRadius='md'
                          bg='gray.50'
                        >
                          <Badge
                            colorScheme='blue'
                            fontSize='lg'
                            mb={2}
                            borderRadius={5}
                          >
                            {ticket.ticketNumber}
                          </Badge>
                          <Grid templateColumns='repeat(3, 1fr)' gap={3}>
                            {renderTicketInfo(
                              'Original Ticket Number',
                              ticket.originalTicketNumber
                            )}
                            {renderTicketInfo('Location', ticket.location)}
                            {renderTicketInfo(
                              'Description',
                              ticket.description
                            )}
                            {renderTicketInfo(
                              'Development Name',
                              ticket.developmentName
                            )}
                            {renderTicketInfo(
                              'Creation Date',
                              ticket.creationDate
                            )}
                            {renderTicketInfo(
                              'Status Description',
                              ticket.statusDescription
                            )}
                            {renderTicketInfo(
                              'Scheduled Date',
                              ticket.scheduledDate
                            )}
                            {renderTicketInfo('Status Date', ticket.statusDate)}
                          </Grid>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </>
              )}
            </Stack>
          )}
        </ModalBody>

        <ModalFooter>
          <Button colorScheme='blue' mr={3} onClick={onClose}>
            Close
          </Button>
          <Button
            variant='outline'
            onClick={() => {
              navigator.clipboard.writeText(workOrderNumber)
              toast({
                title: 'Ticket number copied to clipboard',
                description: `${workOrderNumber} is now in your clipboard`,
                status: 'success',
                duration: 3000,
                isClosable: true,
                position: 'top',
              })
              setTimeout(() => {
                window.open(NYCHA_WORK_ORDER_URL, '_blank')
              }, TOAST_DELAY_MS)
            }}
          >
            View on NYCHA Website
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default NYCHAModal
